import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";
import { 
  PlayIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

// Define the interface inline as per codebase patterns
interface ConversationTree {
  id: number;
  conversation_flow_name: string;
  conversation_flow_version: number;
  is_latest: boolean;
  created_at: string;
  application_name: string;
  intent_name?: string;
  conversation_flow_count?: number;
  status?: string;
}

interface ConversationTreesTableProps {
  conversationTrees: ConversationTree[];
  loading: boolean;
  sortConfig: SortConfig<ConversationTree>;
  onSort: (key: keyof ConversationTree) => void;
  onEdit: (tree: ConversationTree) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
}

// Format status text: convert underscores to spaces, title case
const formatStatus = (status: string | undefined): string => {
  if (!status || status === 'UNKNOWN') return 'Unknown';
  
  // Convert underscores to spaces and title case each word
  return status
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Get status badge color based on status value
const getStatusColor = (status: string | undefined): string => {
  if (!status || status === 'UNKNOWN') {
    return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  }

  const statusLower = status.toLowerCase();
  
  // Active/enabled/ready states - Green
  if (statusLower.includes('enabled') || statusLower.includes('active') || 
      statusLower.includes('ready') || statusLower.includes('completed')) {
    return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
  }
  
  // Disabled/failed states - Red  
  if (statusLower.includes('disabled') || statusLower.includes('failed') || 
      statusLower.includes('error')) {
    return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
  }
  
  // Pending/processing/running states - Yellow
  if (statusLower.includes('pending') || statusLower.includes('processing') || 
      statusLower.includes('running') || statusLower.includes('progress')) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
  }
  
  // Default - Gray
  return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
};

const ConversationTreesTable: React.FC<ConversationTreesTableProps> = ({
  conversationTrees,
  loading,
  sortConfig,
  onSort,
  onEdit,
  currentPage,
  setCurrentPage,
  pageSize,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: string | number) => {
    if (!timestamp) return "Never";
    const date = moment(timestamp);
    const now = moment();
    
    // For dates within the same year, display as three-letter month and date
    if (date.year() === now.year()) {
      return date.format("MMM D");
    }
    // For dates older than one year, display full MM/DD/YYYY format
    return date.format("MM/DD/YYYY");
  };

  const formatTime = (timestamp: string | number) => {
    if (!timestamp) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<ConversationTree>[] = [
    {
      key: "conversation_flow_name",
      header: "Name",
      width: "w-[30%]",
      sortable: true,
      render: (tree) => (
        <div className="space-y-1">
          <div className="font-gc-med text-foreground hover:text-maitai-green transition-colors">
            {tree.conversation_flow_name}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-muted text-foreground font-medium">
              v{tree.conversation_flow_version}
            </span>
            <span>•</span>
            <span>{tree.application_name}</span>
          </div>
        </div>
      )
    },
    {
      key: "intent_name",
      header: "Intent",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div className="text-sm font-gc-reg text-foreground">
          {tree.intent_name || "dev1"}
        </div>
      )
    },
    {
      key: "conversation_flow_count",
      header: "Paths",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div className={cn(
          "text-sm font-gc-reg",
          tree.conversation_flow_count && tree.conversation_flow_count > 0 
            ? "text-foreground" 
            : "text-muted-foreground"
        )}>
          {tree.conversation_flow_count?.toLocaleString() || "5"}
        </div>
      )
    },
    {
      key: "status",
      header: "Status",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getStatusColor(tree.status)
        )}>
          {formatStatus(tree.status)}
        </div>
      )
    },
    {
      key: "created_at",
      header: "Created",
      width: "w-[15%]",
      sortable: true,
      render: (tree) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(tree.created_at)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(tree.created_at)}
          </div>
        </div>
      )
    },
    {
      key: "actions" as keyof ConversationTree,
      header: "",
      width: "w-[15%]",
      sortable: false,
      render: (tree) => (
        <div className="flex items-center justify-center w-full">
          <Button
            variant="ghost"
            size="default"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(tree);
            }}
            className="gap-2 text-sm px-4 py-2 h-10 border border-border hover:bg-accent mx-auto"
          >
            <PlayIcon className="w-4 h-4" />
            Execute
          </Button>
        </div>
      )
    }
  ];

  const defaultSort = (a: ConversationTree, b: ConversationTree) => {
    // Default sort by created_at descending
    const dateA = moment(a.created_at).valueOf();
    const dateB = moment(b.created_at).valueOf();
    return dateB - dateA;
  };

  return (
    <PaginatedTable
      data={conversationTrees}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      emptyTitle="No conversation trees found"
      emptyDescription="Create your first conversation tree to get started with building interactive flows."
      onRowClick={(tree) => navigate(`/admin/conversation-tree?id=${tree.id}`)}
      defaultSort={defaultSort}
    />
  );
};

export default ConversationTreesTable;